"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  formatWorkingHours,
  getDayName,
  groupWorkingHoursByDay,
  isCurrentlyOpen,
} from "@/lib/tenant-landing-v2-utils";
import type { TenantInfo, WorkingHourInfo } from "@/types/tenant-landing-v2";
import { Clock, Info, Mail, MapPin, Phone } from "lucide-react";
import { useMemo } from "react";

interface AboutV2Props {
  tenant: TenantInfo;
  workingHours: WorkingHourInfo[];
}

export function AboutV2({ tenant, workingHours }: AboutV2Props) {
  const groupedHours = useMemo(
    () => groupWorkingHoursByDay(workingHours),
    [workingHours]
  );
  const isOpen = useMemo(() => isCurrentlyOpen(workingHours), [workingHours]);
  const today = new Date().getDay();

  return (
    <section className="px-4 py-4 max-w-screen-xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div className="space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Info className="h-4 w-4" />
              <span className="text-sm font-semibold">Sobre Nós</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Conheça {tenant.name}
            </h2>
          </div>

          {tenant.description && (
            <p className="text-muted-foreground leading-relaxed text-base">
              {tenant.description}
            </p>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações de Contato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {tenant.phone && (
                <a
                  href={`tel:${tenant.phone}`}
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Telefone</p>
                    <p className="font-medium">{tenant.phone}</p>
                  </div>
                </a>
              )}

              {tenant.email && (
                <a
                  href={`mailto:${tenant.email}`}
                  className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">E-mail</p>
                    <p className="font-medium">{tenant.email}</p>
                  </div>
                </a>
              )}

              {tenant.address && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Endereço</p>
                    <p className="font-medium">{tenant.address}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Working Hours */}
        <div>
          <Card className="sticky top-20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Horário de Funcionamento
                </CardTitle>
                {isOpen ? (
                  <Badge className="bg-green-500 hover:bg-green-600 text-white">
                    Aberto
                  </Badge>
                ) : (
                  <Badge variant="secondary">Fechado</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[...Array(7)].map((_, dayOfWeek) => {
                  const dayHours = groupedHours.get(dayOfWeek) || [];
                  const formattedHours = formatWorkingHours(dayHours);
                  const isToday = dayOfWeek === today;

                  return (
                    <div
                      key={dayOfWeek}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isToday
                          ? "bg-primary/10 border-2 border-primary/20"
                          : "bg-muted/30 hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {isToday && (
                          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                        )}
                        <span
                          className={`font-medium ${
                            isToday ? "text-primary" : "text-foreground"
                          }`}
                        >
                          {getDayName(dayOfWeek)}
                        </span>
                      </div>
                      <span
                        className={
                          dayHours.length > 0
                            ? "text-foreground font-medium"
                            : "text-muted-foreground"
                        }
                      >
                        {formattedHours}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
